import { format } from "date-fns";

// TODO enum for common date display formats
// REMEMBER: ISO is a format standard, GMT and UTC are time-keepers
//https://date-fns.org/v4.1.0/docs/format

/**
 * Formats an epoch time in milliseconds using the local system's timezone.
 * Ex output: "Monday May 5th, 2025 at 10:31 pm"
 */
export function formatEpochLocal(epochMs: number): string {
    const localDate = new Date(epochMs);
    return format(localDate, "EEEE MMM do, yyyy 'at' h:mm a");
}

/**
 * Format a UTC ISO-8601 string (e.g. "2024-12-04T22:51:17.387")
 * into "Wednesday Dec 4th, 2024 at h:mm AM/PM" in the requested time-zone.
 *
 * @param utcIso     UTC timestamp without a zone suffix (Z will be added if missing) -- seems to be the case case from server
 * @param timeZoneId IANA zone like "America/Chicago" -- default to the local zone
 * @param locale     BCP-47 locale (default "en-US")
 */
export function formatUtcStringInZone(
    utcIso: string,
    timeZoneId: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: string = "en-US",
): string {
    if (!utcIso.trim()) {
        return ""; // can expand this if needed, but just return empty if no iso provided
    }
    // convert to ISO since server isn't providing it that way
    const stamped = /Z$|[+\-]\d{2}:\d{2}$/.test(utcIso.trim())
        ? utcIso.trim()
        : `${utcIso.trim()}Z`;

    const instant = new Date(stamped); // same moment for everyone
    if (Number.isNaN(instant.getTime())) {
        throw new RangeError(`Invalid ISO-8601 string: “${utcIso}”`);
    }

    const fmt = (opt: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, {
            timeZone: timeZoneId,
            ...opt,
        }).format(instant);

    const weekday = fmt({ weekday: "long" });
    const month = fmt({ month: "short" });
    const day = ordinal(+fmt({ day: "numeric" }));
    const year = fmt({ year: "numeric" });
    const time = fmt({ hour: "numeric", minute: "2-digit", hour12: true });

    return `${weekday} ${month} ${day}, ${year} at ${time}`;
}

/**
 *
 * @param dateStr
 *
 * @returns
 */
export function parseUtcDate(dateStr: string): Date {
    return new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00Z`);
}

/**
 * Format a UTC ISO-8601 string (e.g. "2024-12-04T22:51:17.387")
 * into "Wednesday Dec 4th, 2024 at h:mm AM/PM" in the requested time-zone.
 *
 * @param utcIso     UTC timestamp without a zone suffix (Z will be added if missing) -- seems to be the case case from server
 * @param timeZoneId IANA zone like "America/Chicago" -- default to the local zone
 * @param locale     BCP-47 locale (default "en-US")
 */
export function formatUtcDateStringInZone(
    utcIso: string,
    timeZoneId: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: string = "en-US",
): string {
    if (!utcIso.trim()) {
        return ""; // can expand this if needed, but just return empty if no iso provided
    }
    // convert to ISO since server isn't providing it that way
    const stamped = /Z$|[+\-]\d{2}:\d{2}$/.test(utcIso.trim())
        ? utcIso.trim()
        : `${utcIso.trim()}Z`;

    const instant = new Date(stamped); // same moment for everyone
    if (Number.isNaN(instant.getTime())) {
        throw new RangeError(`Invalid ISO-8601 string: “${utcIso}”`);
    }

    const fmt = (opt: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, {
            timeZone: timeZoneId,
            ...opt,
        }).format(instant);

    const weekday = fmt({ weekday: "long" });
    const month = fmt({ month: "short" });
    const day = ordinal(+fmt({ day: "numeric" }));
    const year = fmt({ year: "numeric" });
    //const time = fmt({ hour: "numeric", minute: "2-digit", hour12: true });

    return `${weekday} ${month} ${day}, ${year}`;
}

// #REGION HELPERS
function ordinal(n: number): string {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
}

/**
 * The Report api gives date back in a non ISO format and provides a timezone offset that
 * determines when a checkin was taken based on the timezone it was taken in. This is the
 * timezone that should be used for display and for bucketing not the system time converted
 * from the utc string
 *
 * I.e. this converts "2025-08-03T04:56:27.180" (UTC) + "-7" => "2025-08-02T21:56:27.180-07:00"
 *
 **/
//
/** Render a UTC instant as local wall time using a fixed offset (e.g. "-7", "-07:00", "+05:30"). */
export function applyZoneToUtcOld(utcIso: string, tzOffset: string): string {
    const pad2 = (n: number) => String(n).padStart(2, "0");
    const pad3 = (n: number) => String(n).padStart(3, "0");

    const parseOffsetMinutes = (s: string): number => {
        const raw = s.trim();
        const sign = raw.startsWith("-") ? -1 : 1;
        const body = raw.replace(/^[-+]/, "");
        if (body.includes(":")) {
            const [h, m = "0"] = body.split(":");
            return (
                sign * ((parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0))
            );
        }
        if (body.includes(".")) {
            const hours = Math.abs(parseFloat(body)) || 0;
            return sign * Math.round(hours * 60);
        }
        if (/^\d{3,4}$/.test(body)) {
            const hh = body.slice(0, body.length - 2);
            const mm = body.slice(-2);
            return (
                sign * ((parseInt(hh, 10) || 0) * 60 + (parseInt(mm, 10) || 0))
            );
        }
        return sign * ((parseInt(body, 10) || 0) * 60);
    };

    // Parse the UTC instant (treat no-zone input as UTC).
    const hasZone = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(utcIso);
    // If a non-UTC zone sneaks in, reject to avoid double-shifting.
    if (hasZone && !/[zZ]$|[+](?:00:?(?:00)?)$/.test(utcIso))
        throw new Error("utcIso must be a UTC instant (Z or +00:00).");

    const epochMs = Date.parse(hasZone ? utcIso : `${utcIso}Z`);
    if (Number.isNaN(epochMs)) throw new Error("Invalid UTC timestamp");

    const offsetMin = parseOffsetMinutes(tzOffset);

    // Compute wall-time fields at that offset (instant stays the same).
    const local = new Date(epochMs + offsetMin * 60_000);
    const y = local.getUTCFullYear();
    const mo = pad2(local.getUTCMonth() + 1);
    const d = pad2(local.getUTCDate());
    const h = pad2(local.getUTCHours());
    const mi = pad2(local.getUTCMinutes());
    const s = pad2(local.getUTCSeconds());
    const ms = pad3(local.getUTCMilliseconds());

    const sign = offsetMin >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMin);
    const offH = pad2(Math.floor(abs / 60));
    const offM = pad2(abs % 60);

    // ISO with explicit offset that encodes the SAME instant.
    return `${y}-${mo}-${d}T${h}:${mi}:${s}.${ms}${sign}${offH}:${offM}`;
}

// Attach (or replace) a zone offset WITHOUT shifting the wall-time.
// e.g. ("2025-08-03T04:56:27.180", "-7") => "2025-08-03T04:56:27.180-07:00"
export function applyZoneToUtc(iso: string, tzOffset: string): string {
    const pad2 = (n: number) => String(n).padStart(2, "0");

    const parseOffsetMinutes = (s: string): number => {
        const raw = s.trim();
        const sign = raw.startsWith("-") ? -1 : 1;
        const body = raw.replace(/^[-+]/, "");

        if (body.includes(":")) {
            const [hStr, mStr = "0"] = body.split(":");
            return (
                sign *
                ((parseInt(hStr, 10) || 0) * 60 + (parseInt(mStr, 10) || 0))
            );
        }
        if (body.includes(".")) {
            const hours = Math.abs(parseFloat(body)) || 0;
            return sign * Math.round(hours * 60); // "+5.5" => +330
        }
        if (/^\d{3,4}$/.test(body)) {
            // "0730" => 7h30m
            const hh = body.slice(0, body.length - 2);
            const mm = body.slice(-2);
            return (
                sign * ((parseInt(hh, 10) || 0) * 60 + (parseInt(mm, 10) || 0))
            );
        }
        return sign * ((parseInt(body, 10) || 0) * 60);
    };

    const mins = parseOffsetMinutes(tzOffset);
    const sign = mins >= 0 ? "+" : "-";
    const abs = Math.abs(mins);
    const hh = pad2(Math.floor(abs / 60));
    const mm = pad2(abs % 60);
    const normalized = `${sign}${hh}:${mm}`;

    // Strip any trailing zone (Z or ±HH[:]?MM) and append normalized offset
    const base = iso.replace(/([Zz]|[+\-]\d{2}:?\d{2})$/, "");
    return `${base}${normalized}`;
}

export function formatIsoForDisplay(iso: string): string {
    const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ] as const;
    const mons = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ] as const;

    // Extract and normalize the trailing zone (Z or ±HH[:]?MM). If missing, assume Z.
    const zoneMatch = iso.match(/([Zz]|[+\-]\d{2}:?\d{2})$/);
    const zone = zoneMatch?.[1] ?? "Z";
    const base = iso.replace(/([Zz]|[+\-]\d{2}:?\d{2})$/, ""); // strip zone for UTC parsing

    // Parse offset minutes from the stored zone
    let offsetMin = 0;
    if (zone.toUpperCase() !== "Z") {
        const m = zone.match(/^([+\-])(\d{2}):?(\d{2})$/);
        if (!m) throw new Error("Invalid ISO offset");
        const sign = m[1] === "-" ? -1 : 1;
        offsetMin = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
    }

    // Parse the base as UTC (ignore any embedded offset for the instant)
    const epoch = Date.parse(`${base}Z`);
    if (Number.isNaN(epoch)) throw new Error("Invalid ISO date string");

    // Convert that UTC instant into the preserved wall-time using the stored offset
    const wall = new Date(epoch + offsetMin * 60_000);

    // Use UTC getters because `wall` is already shifted to the wall timeline
    const dow = days[wall.getUTCDay()];
    const mon = mons[wall.getUTCMonth()];
    const day = wall.getUTCDate();
    const year = wall.getUTCFullYear();

    // Ordinal suffix
    const rem100 = day % 100;
    const suffix =
        rem100 >= 11 && rem100 <= 13
            ? "th"
            : (
                  [
                      "th",
                      "st",
                      "nd",
                      "rd",
                      "th",
                      "th",
                      "th",
                      "th",
                      "th",
                      "th",
                  ] as const
              )[day % 10];

    // 12-hour time
    const h24 = wall.getUTCHours();
    const minute = String(wall.getUTCMinutes()).padStart(2, "0");
    const ampm = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 || 12;

    return `${dow} ${mon} ${day}${suffix}, ${year} at ${h12}:${minute} ${ampm}`;
}

/**
 * Returns the appropriate suffix for a day number
 *
 * @param day - Day of month as string
 * @returns The appropriate suffix (st, nd, rd, or th)
 */
// function getDaySuffix(day: string): string {
//     const dayNum = parseInt(day, 10);

//     if (dayNum > 3 && dayNum < 21) return "th";

//     switch (dayNum % 10) {
//         case 1:
//             return "st";
//         case 2:
//             return "nd";
//         case 3:
//             return "rd";
//         default:
//             return "th";
//     }
// }

// #ENDREGION HELPERS

//export default {};
